import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useGenerateImage } from "recharts-to-png";
import useFormData from "hooks/useFormData";
import getModuleName from "utils/getModuleName";
import { prepareUDTable, prepareAZZKUTable, prepareAZZKUTopTable } from "utils/prepareDataHelpers";
import useUDTopics from "hooks/UD/useUDTopics";
import useUDCompareTopics from "hooks/UD/useUDCompareTopics";
import useEditions from "hooks/UD/useEditions";
import { downloadUdMultiIndicatorZip } from "utils/udMultiIndicatorZipExport";
import useAZZKUTopics from "hooks/AZZKU/useAZZKUTopics";
import useAZZKURelations from "hooks/AZZKU/useAZZKURelations";
import useAZZKUVoivodeships from "hooks/AZZKU/useAZZKUVoivodeships";

import Loader from "components/Loader";
import DownloadSection from "components/DownloadSection";
import ChartsSection from "components/ChartsSection";
import TablesSection from "components/TablesSection";
import Button from "components/Button";
import ResultsHeader from "components/ResultsHeader";
import VisualizationOptionsBox from "./components/VisualizationOptionsBox";
import CitationTemplateBox from "./components/CitationTemplateBox";
import "./results-layout.css";

export default function ResultsLayout({
   init_params,
   init_mode = "chart",
   init_colors = null,
   dataHook,
   SidebarComponent,
   MainComponent,
   onBackToSteps
}) {

   const module = getModuleName();

   const [ mode, setMode ] = useState( init_mode );
   const [ heading, setHeading ] = useState( "" ); 
   const [ hide_sidebar, setHideSidebar ] = useState( false );
   const [ show_sidebar, setShowSidebar ] = useState( false );

   const { data: params, errors, onDataChange: onParamsChange } = useFormData( init_params );

   // Get UD topics for variable name lookup
   const isCompareMode = params?.compare_mode === "compare";
   const { d: topics } = useUDTopics( isCompareMode ? null : params.edition );
   const { d: compareTopics } = useUDCompareTopics( 
      isCompareMode ? params.edition : null, 
      isCompareMode ? params.edition2 : null 
   );
   const displayTopics = isCompareMode ? compareTopics : topics;

   const { d: topicsEdition1 } = useUDTopics(isCompareMode ? params.edition : null);
   const { d: topicsEdition2 } = useUDTopics(isCompareMode ? params.edition2 : null);

   const { d: udEditions } = useEditions();

   const edition1Id = useMemo(
      () => udEditions?.find((e) => e.value === params.edition)?.id ?? null,
      [udEditions, params.edition]
   );
   const edition2Id = useMemo(
      () => udEditions?.find((e) => e.value === params.edition2)?.id ?? null,
      [udEditions, params.edition2]
   );

   const udZipExportActive =
      module === "UD" &&
      Array.isArray(params.key_variable_ids) &&
      params.key_variable_ids.length > 1 &&
      !!edition1Id &&
      (!isCompareMode || !!edition2Id);

   const handleUdZipExport = useCallback(async () => {
      await downloadUdMultiIndicatorZip({
         key_variable_ids: params.key_variable_ids,
         isCompareMode,
         edition1Id,
         edition2Id,
         editionLabel: params.edition,
         edition2Label: params.edition2,
         grouping_variable_ids: params.grouping_variable_ids,
         filters: params.filters,
         displayTopics,
         topicsEdition1,
         topicsEdition2
      });
   }, [
      params.key_variable_ids,
      isCompareMode,
      edition1Id,
      edition2Id,
      params.edition,
      params.edition2,
      params.grouping_variable_ids,
      params.filters,
      displayTopics,
      topicsEdition1,
      topicsEdition2
   ]);
   
   // Get AZZKU topics for variable name lookup
   const { d: azzkuTopics } = useAZZKUTopics();
   
   // Get AZZKU relations for relation name lookup
   const { d: azzkuRelations } = useAZZKURelations( params.variable_id );
   
   // Get AZZKU voivodeships for territory name lookup
   const { d: azzkuVoivodeships } = useAZZKUVoivodeships();

   const [ getDivJpegRaw, { ref: charts_box_ref }] = useGenerateImage({
      quality: 0.8,
      type: 'image/jpeg',
   });

   const getDivJpeg = useCallback(async () => {
      charts_box_ref.current?.classList.add('capturing');
      const result = await getDivJpegRaw();
      charts_box_ref.current?.classList.remove('capturing');
      return result;
   }, [getDivJpegRaw, charts_box_ref]);

   // Refs for a11y focus management
   const sidebarTogglerRef = useRef(null);
   const sidebarContentRef = useRef(null);
   const mainRegionRef = useRef(null);
   const mainContentRef = useRef(null);

   const { data: visualization_options, onDataChange: onVisualizationOptionsChange } = useFormData({
      show_average: false,
      show_main_lines: true,
      show_auxiliary_lines: true
   });

   // W porównywarce UD nie pokazujemy średniej — wymuś wyłączenie, jeśli została włączona wcześniej
   useEffect(() => {
      if (module === "UD" && isCompareMode && visualization_options.show_average) {
         onVisualizationOptionsChange("show_average", false);
      }
   }, [module, isCompareMode, visualization_options.show_average, onVisualizationOptionsChange]);

   const { d, e, loading } = dataHook( params );
   const source = d?.source || d?.[0]?.source || d?.charts?.[0]?.source || "";
   // W trybie porównania API zwraca tablicę bezpośrednio, w trybie single może zwracać obiekt z charts
   const data = Array.isArray(d) ? d : (d?.charts || d);
   const series_colors = d?.series_colors || null;

   const [ custom_colors, setCustomColors ] = useState(init_colors);

   const effective_colors = custom_colors || series_colors || null;

   const onColorChange = useCallback((index, newColor) => {
      setCustomColors(prev => {
         const base = prev || series_colors || [];
         const updated = [...base];
         updated[index] = newColor;
         return updated;
      });
   }, [series_colors]);

   const onColorsReset = useCallback(() => {
      setCustomColors(null);
   }, []);

   // Track changes for aria-live announcements
   const [announcementText, setAnnouncementText] = useState("");
   const prevParamsRef = useRef(null);
   const isInitialMount = useRef(true);

   // Get allow_average and tooltip for UD module
   const allow_average = useMemo(() => {
      if (module !== "UD" || !displayTopics || !params.key_variable_id) return null;
      
      // Konwertuj key_variable_id do liczby dla porównania
      const keyVariableId = Number(params.key_variable_id);
      
      for (let t of displayTopics) {
         let item = null;
         
         if (isCompareMode) {
            // W trybie porównania szukamy w common_key_variables
            const commonVars = t.common_key_variables || [];
            item = commonVars.find(kv => Number(kv.edition1) === keyVariableId);
            // W trybie porównania używamy allow_average z pierwszej edycji
            if (item && item.allow_average !== undefined) {
               return Number(item.allow_average);
            }
         } else {
            item = t.keyVariables?.find(k => Number(k.id) === keyVariableId);
            if (item) {
               // Konwertuj allow_average do liczby
               return Number(item.allow_average);
            }
         }
         
         if (!!t.children?.length) {
            for (let c of t.children) {
               if (isCompareMode) {
                  const childCommonVars = c.common_key_variables || [];
                  const childItem = childCommonVars.find(kv => Number(kv.edition1) === keyVariableId);
                  if (childItem && childItem.allow_average !== undefined) {
                     return Number(childItem.allow_average);
                  }
               } else {
                  const childItem = c.keyVariables?.find(k => Number(k.id) === keyVariableId);
                  if (childItem) {
                     // Konwertuj allow_average do liczby
                     return Number(childItem.allow_average);
                  }
               }
            }
         }
      }
      return null;
   }, [module, displayTopics, params.key_variable_id, isCompareMode]);

   // Get tooltip for UD module key variable
   const key_variable_tooltip = useMemo(() => {
      if (module !== "UD" || !displayTopics || !params.key_variable_id) return null;
      
      const keyVariableId = Number(params.key_variable_id);
      
      for (let t of displayTopics) {
         let item = null;
         
         if (isCompareMode) {
            // W trybie porównania szukamy w common_key_variables
            const commonVars = t.common_key_variables || [];
            item = commonVars.find(kv => Number(kv.edition1) === keyVariableId);
            if (item && item.tooltip) {
               return item.tooltip;
            }
         } else {
            item = t.keyVariables?.find(k => Number(k.id) === keyVariableId);
            if (item && item.tooltip) {
               return item.tooltip;
            }
         }
         
         if (!!t.children?.length) {
            for (let c of t.children) {
               if (isCompareMode) {
                  const childCommonVars = c.common_key_variables || [];
                  const childItem = childCommonVars.find(kv => Number(kv.edition1) === keyVariableId);
                  if (childItem && childItem.tooltip) {
                     return childItem.tooltip;
                  }
               } else {
                  const childItem = c.keyVariables?.find(k => Number(k.id) === keyVariableId);
                  if (childItem && childItem.tooltip) {
                     return childItem.tooltip;
                  }
               }
            }
         }
      }
      return null;
   }, [module, displayTopics, params.key_variable_id, isCompareMode]);

   const tables = useMemo(() => {
      if ( !data || !Array.isArray(data) || data.length === 0 ) return;

      let prepareHelper = (i) => i;
      let variableName = null;
      let territoryName = null;

      switch ( module ) {
         case "UD": 
            prepareHelper = prepareUDTable; 
            // Get variable name for UD
            if (displayTopics && params.key_variable_id) {
               const keyVariableId = Number(params.key_variable_id);
               for (let t of displayTopics) {
                  let item = null;
                  
                  if (isCompareMode) {
                     // W trybie porównania szukamy w common_key_variables
                     const commonVars = t.common_key_variables || [];
                     item = commonVars.find(kv => Number(kv.edition1) === keyVariableId);
                     if (item) {
                        variableName = item.name || "Zmienna";
                        break;
                     }
                  } else {
                     item = t.keyVariables?.find(k => Number(k.id) === keyVariableId);
                     if (item) {
                        variableName = item.name;
                        break;
                     }
                  }
                  
                  if (!!t.children?.length) {
                     for (let c of t.children) {
                        if (isCompareMode) {
                           const childCommonVars = c.common_key_variables || [];
                           const childItem = childCommonVars.find(kv => Number(kv.edition1) === keyVariableId);
                           if (childItem) {
                              variableName = childItem.name || "Zmienna";
                              break;
                           }
                        } else {
                           const childItem = c.keyVariables?.find(k => Number(k.id) === keyVariableId);
                           if (childItem) {
                              variableName = childItem.name;
                              break;
                           }
                        }
                     }
                     if (variableName) break;
                  }
               }
            }
            // If variableName is still null, don't pass it to avoid "null | label" format
            if (!variableName) {
               variableName = null;
            }
            break;
         case "AZZKU": 
            prepareHelper = prepareAZZKUTable; 
            // Get variable name or relation name for AZZKU
            if (params.relation_id && azzkuRelations) {
               // If relation_id is set, use relation name
               const relation = azzkuRelations.find(r => +r.value === +params.relation_id);
               if (relation) {
                  variableName = relation.label;
               }
            } else if (azzkuTopics && params.variable_id) {
               // Otherwise, use variable name
               for (let topic of azzkuTopics) {
                  const variable = topic.variables?.find(v => +v.value === +params.variable_id);
                  if (variable) {
                     variableName = variable.label;
                     break;
                  }
               }
            }
            
            // Get territory name for AZZKU
            if (azzkuVoivodeships) {
               if (params.voivodeship_ids && params.voivodeship_ids.length > 0) {
                  const territoryNames = params.voivodeship_ids.map(id => {
                     return azzkuVoivodeships.find(v => +v.value === id)?.label;
                  }).filter(Boolean);
                  if (territoryNames.length > 0) {
                     territoryName = territoryNames.join(", ");
                  }
               } else {
                  territoryName = "Polska";
               }
            }
            
            // If variableName is still null, don't pass it to avoid "null | label" format
            if (!variableName) {
               variableName = null;
            }
            break;
         case "AZZKU_TOP": prepareHelper = prepareAZZKUTopTable; break;
      }

      return data.map( item => {
         // W trybie porównania przekazujemy dodatkowe parametry do prepareUDTable
         if (module === "UD" && isCompareMode) {
            return prepareHelper( item, variableName, territoryName, isCompareMode, params.edition, params.edition2 );
         }
         return prepareHelper( item, variableName, territoryName );
      });
   }, [ data, displayTopics, params.key_variable_id, module, azzkuTopics, params.variable_id, azzkuRelations, params.relation_id, azzkuVoivodeships, params.voivodeship_ids, isCompareMode, params.edition, params.edition2 ]);
   
   useEffect(() => {
      const search = new URLSearchParams();

      Object.keys(params).forEach(k => {
         if (k === "filters") {
            const rawFilters = params[k] || {};
            const cleaned = Object.fromEntries(
               Object.entries(rawFilters).filter(([_, v]) => {
                  if (v === null || v === undefined) return false;
                  if (Array.isArray(v)) return v.length > 0;
                  if (typeof v === 'object') return Object.keys(v).length > 0;
                  return true;
               })
            );
            if (Object.keys(cleaned).length > 0) {
               search.append(k, JSON.stringify(cleaned));
            }
         } else {
            if (Array.isArray(params[k]) && !params[k].length) return;
            if (params[k] !== undefined && params[k] !== null && params[k] !== "") {
               search.append(k, params[k]);
            }
         }
      });

      search.append("mode", mode);

      if (custom_colors && custom_colors.length > 0) {
         search.append("colors", custom_colors.map(c => c.replace("#", "")).join(","));
      }

      window.history.replaceState(null, null, "?" + search.toString());
   }, [ params, mode, custom_colors ])

   // Announce results changes to screen readers
   useEffect(() => {
      // Skip announcement on initial mount
      if (isInitialMount.current) {
         isInitialMount.current = false;
         prevParamsRef.current = JSON.stringify(params);
         return;
      }

      // Check if params actually changed
      const currentParamsStr = JSON.stringify(params);
      const paramsChanged = prevParamsRef.current !== currentParamsStr;
      
      if (paramsChanged) {
         prevParamsRef.current = currentParamsStr;
         
         if (!loading && data && (Array.isArray(data) ? data.length > 0 : true)) {
            // Announce when results are ready after parameter change
            setAnnouncementText("Wyniki wyboru przeglądarka danych o uczeniu się dorosłych");
         } else if (loading) {
            // Clear announcement while loading
            setAnnouncementText("");
         }
      }
   }, [params, data, loading])

   useEffect(() => {
      document.body.style.overflow = show_sidebar ? "hidden" : "unset";
      if ( !show_sidebar ) window.scrollTo({ top: 0, behavior: "smooth" });

      // Manage inert/aria-hidden and focus when sidebar opens (mobile overlay)
      const mainEl = mainContentRef.current; // Używamy mainContentRef zamiast mainRegionRef, aby nie blokować modala
      const panelEl = sidebarContentRef.current;

      if (show_sidebar) {
         if (mainEl) {
            mainEl.setAttribute('aria-hidden', 'true');
            // @ts-ignore: inert might not exist in TS lib
            mainEl.inert = true;
         }

         // Focus first focusable in panel
         if (panelEl) {
            const focusable = panelEl.querySelector(
               'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable && focusable.focus) {
               focusable.focus();
            } else {
               panelEl.setAttribute('tabindex', '-1');
               panelEl.focus();
            }
         }
      } else {
         if (mainEl) {
            mainEl.removeAttribute('aria-hidden');
            // @ts-ignore
            mainEl.inert = false;
         }
         // Restore focus to toggler after closing
         if (sidebarTogglerRef.current && sidebarTogglerRef.current.focus) {
            sidebarTogglerRef.current.focus();
         }
      }

      // ESC to close overlay
      const onKeyDown = (e) => {
         if (e.key === 'Escape' && show_sidebar) {
            setShowSidebar(false);
         }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, [ show_sidebar ])

   return (
      <div className="results-layout" role="main" id="main-content" ref={ mainRegionRef }>

         {/* Live region for screen reader announcements */}
         <div aria-live="polite" aria-atomic="true" className="sr-only" id="live-region">
            { show_sidebar ? "Otworzono panel filtrów" : "" }
            { announcementText && <span key={Date.now()}>{announcementText}</span> }
         </div>

         <div 
            className={ "results-layout-sidebar " + ( show_sidebar ? "visible" : "" ) + ( hide_sidebar ? " hidden" : "" )} 
            role="complementary" 
            aria-label="Panel filtrów" 
            id="navigation"
            onClick={(e) => {
               // Zamknij modal po kliknięciu w tło (ale nie w zawartość)
               if (show_sidebar && e.target === e.currentTarget) {
                  setShowSidebar(false);
               }
            }}
         >

            <div
               role="button" 
               className="results-layout-sidebar-toggler"
               onClick={(e) => {
                  e.stopPropagation();
                  setHideSidebar( h => !h );
               }}
               onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHideSidebar(h => !h); }}}
               aria-expanded={!hide_sidebar}
               aria-controls="filters-panel"
               aria-label={hide_sidebar ? "Pokaż panel filtrów" : "Ukryj panel filtrów"}
               tabIndex="0"
               ref={ sidebarTogglerRef }
            >
               <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect width="36" height="36" rx="4" fill="#00258B"/>
                  <path d="M20 12L14 18L20 24" stroke="white" strokeWidth="2" strokeLinecap="round"/>
               </svg>

               Filtrowanie
            </div>

            <div 
               className="results-layout-sidebar-content" 
               id="filters-panel" 
               ref={ sidebarContentRef }
               onClick={(e) => e.stopPropagation()}
            >

               <div 
                  role="button" 
                  className="close" 
                  aria-label="Zamknij panel filtrów" 
                  tabIndex="0" 
                  onKeyDown={(e) => { 
                     if (e.key === 'Enter' || e.key === ' ') { 
                        e.preventDefault(); 
                        e.stopPropagation();
                        setShowSidebar(false); 
                     }
                  }} 
                  onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     setShowSidebar(false);
                  }}
               >
                  <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M2 17.5L18 1.5" stroke="#00258B" strokeWidth="3"/>
                     <path d="M2 1.5L18 17.5" stroke="#00258B" strokeWidth="3"/>
                  </svg>
               </div>

               { SidebarComponent &&
                  <SidebarComponent
                     params={ params }
                     errors={ errors }
                     onChange={ onParamsChange }
                  />
               }

               <Button
                  label="Generuj raport"
                  onClick={() => setShowSidebar( false )}
               />
            </div>
         </div>

         <div className="results-layout-main" ref={ mainContentRef }>
            <div className="box"> 

               { module !== "AZZKU_TOP" 
                  ? <ResultsHeader
                     heading={ heading }
                     mode={ mode }
                     onModeChange={ m => setMode( m )}
                     onBackToSteps={ module === "UD" ? onBackToSteps : undefined }
                  /> : <h3> { heading } </h3>
               }

               { MainComponent && 
                  <MainComponent
                     heading={ heading }
                     mode={ mode }
                     params={ params }
                     setHeading={ setHeading }
                     onChange={ onParamsChange }
                  />
               }

               { loading && <Loader />}

               { e && 
                  <div style={{ color: "red" }}>
                     { e &&
                        <>
                           
                           { e === "Years parameter is required for raw values_type" && 
                              "Proszę wybrać rok"
                           }

                           { e === "base_year and compare_year are required for dynamics_change" &&
                              "Wybierz lata z dostępnych powyżej"
                           }

                           { e === "The profession ids field is required." &&
                              "Proszę wybrać zawód"
                           }

                           { e === "The voivodeship ids field is required." && 
                              "Proszę wybrać województwo"
                           }
                        </>
                     } 
                  </div>
               }

               <div
                  ref={ charts_box_ref }
                  className="charts-section"
                  style={{
                     display: mode === "chart" ? "flex" : "none",
                     padding: "10px 10px 0",
                     flexDirection: "column",
                     gap: "20px"
                  }}
               >
                  { mode === "chart" &&
                     <ChartsSection
                        charts={ data }
                        mainHeading={ heading }
                        series_colors={ effective_colors }
                        allowed_colors={ series_colors }
                        visualization_options={ visualization_options }
                        key_variable_tooltip={ key_variable_tooltip }
                        isCompareMode={ isCompareMode }
                        edition1={ params.edition }
                        edition2={ params.edition2 }
                        loading={ loading }
                        onColorChange={ onColorChange }
                     />
                  }
               </div>

               { mode === "table" &&
                  <TablesSection tables={ tables } mainHeading={ heading } />
               }

               { data && Array.isArray(data) && data.length > 0 &&
                  <DownloadSection
                     mode={ mode }
                     source={ source }
                     tables={ tables }
                     getDivJpeg={ getDivJpeg }
                     chart_footer={ source ? "Źródło danych: " + source : undefined }
                     udZipExport={
                        udZipExportActive
                           ? { active: true, exportZip: handleUdZipExport }
                           : null
                     }
                  />
               }
            </div>

            <Button
               extra_classes="sidebar-btn"
               label="Zmień kryteria wyszukiwania"
               onClick={() => setShowSidebar( true )}
            />

            { !loading && data && Array.isArray(data) && data.length > 0 &&
               <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}> 
                  { mode === "chart" && 
                     <VisualizationOptionsBox
                        data={ visualization_options }
                        onChange={ onVisualizationOptionsChange }
                        allow_average={ allow_average }
                        hasCustomColors={ !!custom_colors }
                        onColorsReset={ onColorsReset }
                        isCompareMode={ isCompareMode }
                     />
                  }

                  <CitationTemplateBox 
                     heading={ heading }
                     source={ source }   
                  />
               </div>
            }
         </div>
      </div>
   )
}